import {
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
} from "lucide-react";
import type { ApAttachmentStatus } from "@/accounts-payable/types";
import type { invoiceStatusMeta } from "@/accounts-payable/utils";

export const INVOICE_STATUS_TEXT_CLASS: Record<
  ReturnType<typeof invoiceStatusMeta>["variant"],
  string
> = {
  blue: "text-blue-70",
  aqua: "text-aqua-70",
  gold: "text-gold-70",
  purple: "text-purple-70",
  orange: "text-orange-70",
  magenta: "text-magenta-70",
  red: "text-red-70",
  green: "text-green-70",
  gray: "text-tertiary-text",
};

/**
 * What happened to an attachment that produced no invoices. Said once, in
 * words; an attachment that did produce invoices shows them instead.
 */
export const NO_INVOICE_OUTCOME: Record<
  ApAttachmentStatus,
  { text: string; negative?: boolean }
> = {
  pending: { text: "Waiting to be checked." },
  classifying: { text: "Checking whether this is an invoice…" },
  is_invoice: { text: "Reading the invoice…" },
  // Receipts live in their own tab; the email only notes where this one went.
  is_receipt: { text: "A receipt — it's in the Receipts tab." },
  not_invoice: { text: "Not an invoice, skipped." },
  download_failed: {
    text: "Download failed. We couldn’t get this file from the email.",
    negative: true,
  },
  classification_failed: {
    text: "We couldn’t tell whether this is an invoice.",
    negative: true,
  },
  unsupported_type: {
    text: "Unsupported file type.",
    negative: true,
  },
};

/** Lucide's file-type icons: the document shape tells PDF from photo at a glance. */
export const fileIconFor = (contentType: string) => {
  if (contentType === "application/pdf" || contentType.startsWith("text/"))
    return FileText;
  if (contentType.startsWith("image/")) return FileImage;
  if (/spreadsheet|excel|csv/.test(contentType)) return FileSpreadsheet;
  if (/zip|compressed|archive/.test(contentType)) return FileArchive;
  return File;
};

export const missingInvoicesMessage = (
  expected: number,
  loaded: number,
  loading: boolean,
): string | null => {
  if (loaded >= expected) return null;
  if (loading) return "Loading extracted invoices…";
  if (loaded === 0) return "Couldn’t load the extracted invoices.";
  return `Couldn’t load ${expected - loaded} of the ${expected} extracted invoices.`;
};
